'use client';

import Image from 'next/image';
import { X } from 'lucide-react';
import { APP_BANNER_DISMISS_KEY, PLAY_STORE_URL, getAppHref } from '@/lib/app-links';

// "Get the app" strip above the site header on phones. Rendered for every
// visitor but shown only where components/app-banner-gate.tsx marked the
// device (see the .app-banner rule in globals.css), one variant per store.
// Pinned at the top of the screen until the customer closes it (on public
// pages just under the news bar, via --ticker-h); the header and the other
// sticky bars sit under it via --app-banner-h, which must match the fixed
// h-17 here.
const STORES = [
  { platform: 'ios', href: getAppHref('ios', 'banner') },
  ...(PLAY_STORE_URL ? [{ platform: 'android', href: getAppHref('android', 'banner') }] : []),
];

function dismiss() {
  try {
    localStorage.setItem(APP_BANNER_DISMISS_KEY, String(Date.now()));
  } catch {
    // Storage blocked: the banner closes now and returns on the next visit.
  }
  document.documentElement.removeAttribute('data-app-banner');
}

export function AppBanner() {
  return STORES.map((store) => (
    <aside
      key={store.platform}
      data-platform={store.platform}
      aria-label="Frags For Less app"
      className="app-banner sticky top-[var(--ticker-h,0px)] z-30 h-17 border-b border-[var(--border)] bg-[var(--background)]"
    >
      <div className="mx-auto flex h-full w-full max-w-7xl items-center gap-2 pl-2 pr-4 md:px-6">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close app banner"
          className="grid size-8 shrink-0 place-items-center rounded-full text-[var(--text-muted)] transition-colors hover:bg-[var(--row-hover)]"
        >
          <X className="size-4" />
        </button>
        <a
          href={store.href}
          data-placement="banner"
          className="flex min-w-0 flex-1 items-center gap-3"
        >
          <Image
            src="/app/app-icon.png"
            alt=""
            width={48}
            height={48}
            className="size-12 shrink-0 rounded-[11px] border border-[var(--border)]"
          />
          <span className="min-w-0 flex-1">
            <span className="font-display block truncate text-[15px] font-semibold leading-tight text-[var(--foreground)]">
              Catch new stock first
            </span>
            <span className="mt-0.5 block truncate text-xs leading-snug text-[var(--text-muted)]">
              Instant alerts in our free app
            </span>
          </span>
          <span className="font-display shrink-0 rounded-full bg-[var(--sidebar-bg)] px-5 py-2 text-sm font-semibold text-white">
            Get
          </span>
        </a>
      </div>
    </aside>
  ));
}
